import Vue from "vue"
import Router from "vue-router"
import { provider } from "@/provider.js"

// App containers
import AppContainer from "@/components/app-containers/AppContainer"
import AuthContainer from "@/components/app-containers/AuthContainer"
import KitchenSinkContainer from "@/components/app-containers/KitchenSinkContainer"

import AccountConfig from "@/components/accounts/account-config/AccountConfig.vue"
import SystemEmailTemplate from "@/components/accounts/account-config/SystemEmailTemplate.vue"
import Users from "@/components/Users/Users"
import UserEdit from "@/components/Users/UserEdit/UserEdit"
import UserProfile from "@/components/Users/UserEdit/UserProfile"
import NewUser from "@/components/Users/NewUser"
import Teams from "@/components/teams/Teams"
import TeamEdit from "@/components/teams/TeamEdit"
import NewTeam from "@/components/teams/NewTeam"
import DashboardOverview from "@/components/DashboardOverview"
import DashboardTimeline from "@/components/DashboardTimeline"
import AuditLog from "@/components/AuditLog"
import TasksTable from "@/components/homepage/TasksTable"
import Jobs from "@/components/jobs/Jobs"
import Chat from "@/components/chat/ChatContainer"
import Schema from "@/components/Schema"
import BucketNew from "@/components/buckets/bucket-edit/BucketNew"
import BucketEdit from "@/components/buckets/bucket-edit/BucketEdit"
import SearchResults from "@/components/search/SearchResults"
import SearchFocused from "@/components/search/SearchFocused"
import CaseTemplateOverview from "@/components/case-templates/CaseTemplateOverview"
import BucketGroup from "@/components/case-templates/BucketGroup"
import BucketGroupEdit from "@/components/buckets/bucket-edit/BucketGroupEdit"
import CaseNew from "@/components/cases/CaseNew"
import CaseEdit from "@/components/cases/case-edit/CaseEdit"
import CaseConfigOverview from "@/components/cases/case-config/CaseConfigOverview"
import CaseConfigContainer from "@/components/cases/case-config/CaseConfigContainer"
import CaseTimeline from "@/components/cases/case-edit/CaseTimeline"
import CaseConversations from "@/components/cases/case-edit/CaseConversations"
import CaseNotes from "@/components/cases/case-edit/CaseNotes"
import CaseParticipants from "@/components/cases/case-edit/CaseParticipants"
import CaseDocuments from "@/components/cases/case-edit/CaseDocuments"
import CaseTasks from "@/components/cases/case-edit/CaseTasks"
import CaseRelations from "@/components/cases/case-edit/CaseRelations"
import CustomDomain from "@/components/CustomDomain"
import ImportWizard from "@/components/import/importWizard.vue"
import createStore from "@/store.js"
import Login from "@/components/Login"
import SsoCallback from "@/components/SsoCallback"
import MFAuth from "@/components/MFAuth"
import OAuthAuthorize from "@/components/OAuthAuthorize"
import Script from "@/components/cases/case-config/Script"
import EmailTemplate from "@/components/cases/case-config/EmailTemplate"
import Tasks from "@/components/homepage/Tasks"
import ForgotPassword from "@/components/ForgotPassword"
import ResetPassword from "@/components/ResetPassword"
import Bucket from "@/components/buckets/BucketView"
import KitchenSink from "@/components/kitchen-sink/KitchenSink"
import Notifications from "@/components/notifications/Notifications"
import ServiceLevels from "@/components/service-level/ServiceLevels"
import CommsInbox from "@/components/comms/CommsInbox"
import MetricsDashboard from "@/components/metrics/MetricsDashboard"
import MetricsAdmin from "@/components/metrics/admin/Metrics"
import QueueDashboard from "@/components/admin/QueueDashboard"
import KanbanBoard from "@/components/kanban/KanbanBoard"

import ChatSession from "@/components/chat/ChatSession"
import ChatIntro from "@/components/chat/ChatIntro"

const JOBS_PAGE_SIZE = 10

const store = createStore(provider)
Vue.use(Router)

// Determine base path - use /xg/ for production and local development
// This matches the publicPath in vue.config.js
const getBasePath = () => {
  // Check if we're running under /xg path
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/xg')) {
    return '/xg/'
  }
  // Default to /xg/ for consistency
  return '/xg/'
}

const router = new Router({
  base: getBasePath(),
  routes: [
    {
      path: "/",
      beforeEnter: (to, from, next) => {
        store.dispatch("setLoading", true)
          .then(() => store.dispatch("requestCurrentUser"))
          .then(() => store.dispatch("reloadTeamsAndUsers"))
          .then(() => store.dispatch("reloadCaseTypes"))
          .then(() => Promise.all([
            store.dispatch("refreshNotifications"),
            store.dispatch("subscribeToNotifications")
          ]))
          .then(() => store.dispatch("setLoading", false))
          .then(() => next())
          .catch(() => {
            store.dispatch("setLoading", false)
            next()
          })
      },
      component: AppContainer, // All loggedin areas
      children: [
        {
          path: "tasks",
          name: "Tasks",
          component: Tasks,
        },
        {
          path: "users",
          beforeEnter: (to, from, next) => {
            store.dispatch("reloadTeamsAndUsers").then(() => next())
          },
          name: "Users",
          component: Users,
        },
        {
          path: "users/new",
          name: "NewUser",
          component: NewUser,
        },
        {
          path: "users/:id/:tab",
          beforeEnter: (to, from, next) => {
            store.dispatch("setCurrentlySelectedUser", parseInt(to.params.id))
            store.dispatch("reloadTeamsAndUsers").then(() => next())
          },
          name: "UserEdit",
          props: true,
          component: UserEdit,
        },
        {
          path: "admin/sla",
          beforeEnter: (to, from, next) => {
            store.dispatch("reloadAccount").then(() => next())
          },
          name: "ServiceLevels",
          component: ServiceLevels,
        },
        {
          path: "admin/metrics",
          beforeEnter: (to, from, next) => {
            store.dispatch("reloadAccount")
            store.dispatch("reloadMetrics").then(() => next())
          },
          name: "MetricsAdmin",
          component: MetricsAdmin,
        },
        {
          path: "admin/queues",
          beforeEnter: (to, from, next) => {
            const user = store.state.currentUser
            const email = user && (user.email || (user.me && user.me.email))
            const at = typeof email === "string" ? email.lastIndexOf("@") : -1
            const caseblocksEmail = at !== -1 && email.slice(at + 1).toLowerCase() === "caseblocks.com"
            if (!user || !user.isAccountAdmin || !caseblocksEmail) {
              next("/")
              return
            }
            next()
          },
          name: "QueueDashboard",
          component: QueueDashboard,
        },
        {
          path: "/dashboard/jobs",
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            const pageFromQuery = to.query.page && parseInt(to.query.page, 10)
            const page = (pageFromQuery >= 1) ? pageFromQuery - 1 : 0
            const pageSize = JOBS_PAGE_SIZE
            store.dispatch("getJobsPage", { page, pageSize, myJobsOnly: true }).then(() => next())
          },
          name: "Jobs",
          component: Jobs,
        },
        {
          path: "/dashboard/chat",
          name: "Chat",
          component: Chat,
          children: [
            { path: "", name: "EmptyChatSession", component: ChatIntro },
            {
              path: "session/:sessionId",
              name: "ChatSession",
              component: ChatSession,
            },
          ],
        },
        {
          path: "/dashboard/boards",
          name: "BoardsList",
          component: () => import("@/components/kanban/BoardsList.vue"),
        },
        {
          path: "/dashboard/boards/:boardId",
          name: "KanbanBoard",
          component: () => import("@/components/kanban/KanbanBoardWrapper.vue"),
          beforeEnter: (to, from, next) => {
            const boardId = parseInt(to.params.boardId)
            const board = store.state.kanban.boards.find(b => b.id === boardId)
            if (board) {
              store.commit('kanban/setCurrentBoard', board)
              next()
            } else {
              next('/dashboard/boards')
            }
          }
        },
        {
          path: "/dashboard/metrics",
          beforeEnter: (to, from, next) => {
            store.dispatch("reloadMetrics").then(() => store.dispatch("reloadWidgets").then(() => store.dispatch("refreshNotifications").then(() => next())))
          },
          name: "Metrics",
          component: MetricsDashboard,
        },
        {
          path: "teams",
          beforeEnter: (to, from, next) => {
            store.dispatch("reloadTeamsAndUsers").then(() => next())
          },
          name: "Teams",
          component: Teams,
        },
        {
          path: "teams/:id/:tab",
          beforeEnter: (to, from, next) => {
            store.dispatch("reloadTeamsAndUsers").then(() => store.dispatch("setCurrentTeam", parseInt(to.params.id)).then(() => next()))
          },
          name: "TeamEdit",
          props: true,
          component: TeamEdit,
        },
        {
          path: "teams/new",
          name: "NewTeam",
          component: NewTeam,
        },
        {
          path: "/dashboard/tasks",
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            next()
          },
          name: "TasksTable",
          component: TasksTable,
        },
        {
          path: "/dashboard/timeline",
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("setupTimeline", { timelineType: "user" })
            store.dispatch("requestUserTimeline")
            next()
          },
          name: "DashboardTimeline",
          component: DashboardTimeline,
        },
        {
          path: "/dashboard/audit",
          redirect: "/admin/audit",
        },
        {
          path: "admin/audit",
          beforeEnter: (to, from, next) => {
            const user = store.state.currentUser
            const account = user && user.account
            if (!user || !user.isAccountAdmin || !account || !account.auditEnabled) {
              next("/")
              return
            }
            store.dispatch("switchMenu", { menu: "config", from, to }).then(() => next())
          },
          name: "AuditLog",
          component: AuditLog,
        },
        {
          path: "/dashboard/notifications",
          name: "Notifications",
          component: Notifications,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("setupTimeline", { timelineType: "user" })
            store.dispatch("requestUserTimeline")
            next()
          },
        },
        {
          path: "/dashboard/comms/:saved_search_id",
          name: "CommsInbox",
          component: CommsInbox,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("switchMenu", { menu: "main", from, to })
            const pageSize = store.state.currentUser.me.preferences.pageSize
            store.dispatch("reloadSavedSearches", {
              currentPage: 0,
              pageSize,
              savedSearchId: to.params.saved_search_id,
            })
            next()
          },
        },
        {
          path: "/dashboard/overview",
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            if (store.state.ui && store.state.ui.commsEnabled) {
              store.dispatch("reloadSavedSearchesTotals")
            }
            store.dispatch("switchMenu", { menu: "main", from, to }).then(() => next())
          },
          name: "Dashboard",
          component: DashboardOverview,
        },
        {
          path: "/dashboard/user-profile/:tab",
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            const userId = store.state.currentUser.me.id
            store.dispatch("setCurrentlySelectedUser", parseInt(userId))
            next()
          },
          name: "UserProfile",
          props: true,
          component: UserProfile,
        },
        {
          path: "/schema",
          name: "Schema",
          component: Schema,
        },
        {
          path: "/case-types/:case_type_id/buckets/new",
          beforeEnter: (to, from, next) => {
            store.dispatch("recordLastCaseContainer", from.path)
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            store.dispatch("clearBucket")
            store.dispatch("setSchema", to.params.case_type_id)
            store.dispatch("prepareNewBucket")
            next()
          },
          name: "BucketNew",
          component: BucketNew,
        },
        {
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main", from, to })
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            store.dispatch("checkBucketBookmark", to.fullPath)
            next()
          },
          path: "/case-types/:case_type_id/buckets/:bucket_id",
          name: "Bucket",
          component: Bucket,
        },
        {
          path: "/case-types/:case_type_id/overview",
          name: "CaseTemplateOverview",
          component: CaseTemplateOverview,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main", from, to }).then(() => store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id }).then(() => next()))
          },
        },
        {
          path: "/case-types/:case_type_id/import",
          name: "Import",
          component: ImportWizard,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main", from, to })
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            next()
          },
        },
        {
          path: "/case-types/:case_type_id/bucket-groups/:bucket_group_id/edit",
          name: "BucketGroupEdit",
          component: BucketGroupEdit,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            next()
          },
        },
        {
          path: "/case-types/:case_type_id/bucket-groups/new",
          name: "BucketGroupNew",
          component: BucketGroupEdit,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            next()
          },
        },
        {
          path: "/case-types/:case_type_id/bucket-groups/:bucket_group_id",
          name: "BucketGroup",
          component: BucketGroup,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            store.dispatch("switchMenu", { menu: "main", from, to })
            next()
          },
        },
        {
          path: "/case/:case_type_id/new",
          beforeEnter: async (to, from, next) => {
            store.dispatch("recordLastCaseContainer", from.path)
            store.dispatch("setCurrentCaseTemplate", { caseTypeId: to.params.case_type_id })
            store.dispatch("prepareNewCase", { caseTypeId: to.params.case_type_id })
            next()
          },
          name: "CaseNew",
          component: CaseNew,
        },
        {
          path: "/search/:search_term",
          name: "SearchResults",
          component: SearchResults,
          beforeEnter: async (to, from, next) => {
            store.dispatch("refreshNotifications").then(() => next())
          },
        },
        {
          path: "/search/:case_type_id/:search_term",
          name: "SearchFocused",
          component: SearchFocused,
        },
        {
          path: "/case-types/:case_type_id/buckets/:bucket_id/edit",
          name: "BucketEdit",
          component: BucketEdit,
          beforeEnter: (to, from, next) => {
            const caseTypeId = parseInt(to.params.case_type_id)
            const caseType = store.state.caseTypes.all.find((ct) => ct.id === caseTypeId)
            store.dispatch("refreshNotifications")

            store.dispatch("setCurrentCaseTemplate", { caseTypeId: caseTypeId })
            // store.dispatch('clearBucket')
            store.dispatch("setSchema", caseTypeId)
            store.dispatch("getBucket", { caseType, bucketId: parseInt(to.params.bucket_id), page: 1, pageSize: 1 }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/details",
          name: "Case",
          component: CaseEdit,
          props: { enableTimer: true },
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("recordLastCaseContainer", from.path)
            store.dispatch("refreshRecentCases")
            store.dispatch("checkCaseBookmark", to.fullPath)
            store.dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
            store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next())
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/timeline",
          name: "CaseTimeline",
          component: CaseTimeline,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            store.dispatch("setupTimeline", { timelineType: "case" })
            store.dispatch("checkCaseBookmark", to.fullPath)
            store.dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
            store.dispatch("getCurrentlySelectedCaseTimeline", { caseId: to.params.case_id })
            store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next())
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("setupTimeline", { timelineType: "user" })
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/conversations",
          name: "CaseConversations",
          component: CaseConversations,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            store.dispatch("checkCaseBookmark", to.fullPath)
            store.dispatch("recordLastCaseContainer", from.path)
            // Load case before mount: default state has case: {} (no id), so CaseConversations
            // would skip reloadMessages and never fetch conversations.
            store
              .dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
              .then(function () {
                return store.dispatch("switchMenu", { menu: "case", from, to })
              })
              .then(function () {
                next()
              })
              .catch(function () {
                next()
              })
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/notes",
          name: "CaseNotes",
          component: CaseNotes,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            store.dispatch("checkCaseBookmark", to.fullPath)
            store.dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
            store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next())
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/participants",
          name: "CaseParticipants",
          component: CaseParticipants,
          beforeEnter: (to, from, next) => store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next()),
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/documents/:path_navigation(.*)?",
          name: "CaseDocuments",
          component: CaseDocuments,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            store.dispatch("checkCaseBookmark", to.fullPath)
            store.dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
            store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next())
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/tasks",
          name: "CaseTasks",
          component: CaseTasks,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            store.dispatch("checkCaseBookmark", to.fullPath)
            store.dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
            store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next())
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "/case/:case_id/related-:related_type",
          name: "CaseRelations",
          component: CaseRelations,
          beforeEnter: (to, from, next) => {
            store.dispatch("refreshNotifications")
            store.dispatch("refreshRecentCases")
            store.dispatch("getCurrentlySelectedCase", { caseId: to.params.case_id })
            store.dispatch("switchMenu", { menu: "case", from, to }).then(() => next())
          },
          beforeLeave: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main" }).then(() => next())
          },
        },
        {
          path: "admin/case_types",
          name: "CaseConfigOverview",
          component: CaseConfigOverview,
          beforeEnter: async (to, from, next) => {
            await store.dispatch("reloadCaseTypes")
            store.dispatch("switchMenu", { menu: "config", from, to }).then(() => next())
          },
        },
        {
          path: "admin/case_types/new",
          name: "NewCaseTemplate",
          component: CaseConfigContainer,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "config" })
            store.dispatch("prepareNewCaseTemplate").then(() => next())
          },
        },
        {
          path: "admin/case_types/:case_type_id/:config_tab",
          name: "CaseConfig",
          component: CaseConfigContainer,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "config" })
            store.dispatch("reloadTeamsAndUsers")
            store.dispatch("setupCaseType", parseInt(to.params.case_type_id)).then(() => next())
          },
        },
        {
          path: "admin/case_types/:case_type_id/Scripts/:script_id",
          name: "Script",
          component: Script,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "config" })
            store.dispatch("setupCaseType", parseInt(to.params.case_type_id)).then(() => next())
          },
        },
        {
          path: "admin/case_types/:case_type_id/emails/:email_id",
          name: "Email",
          component: EmailTemplate,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "config" })
            store.dispatch("setupCaseType", parseInt(to.params.case_type_id)).then(() => next())
          },
        },
        {
          path: "account-menu/system-email/:email_id",
          name: "SystemEmailTemplate",
          component: SystemEmailTemplate,
          beforeEnter: (to, from, next) => {
            const user = store.state.currentUser
            if (!user || !user.isAccountAdmin) {
              next("/")
              return
            }
            store.dispatch("switchMenu", { menu: "config" })
            next()
          },
        },
        {
          path: "account-menu/:account_tab",
          name: "AccountConfig",
          component: AccountConfig,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "config" })
            store.dispatch("reloadAccount").then(() => {
              if (to.params.account_tab === "features") {
                const user = store.state.currentUser
                if (!user || !user.isAppAdmin) {
                  next({ name: "AccountConfig", params: { account_tab: "details" }, replace: true })
                  return
                }
              }
              next()
            })
          },
        },
        {
          path: "kanban",
          name: "KanbanBoard",
          component: KanbanBoard,
          beforeEnter: (to, from, next) => {
            store.dispatch("switchMenu", { menu: "main", from, to })
            next()
          },
        },
      ],
    },
    {
      path: "/oauth/authorize",
      component: AuthContainer,
      children: [
        {
          path: "",
          name: "OAuthAuthorize",
          component: OAuthAuthorize,
        },
      ],
    },
    {
      path: "/oauth/mcp/authorize",
      redirect: "/oauth/authorize",
    },
    {
      path: "/login",
      component: AuthContainer, // All logged out areas
      children: [
        {
          path: "",
          name: "Login",
          component: Login,
        },
        {
          path: "sso-callback",
          name: "SsoCallback",
          component: SsoCallback,
        },
        {
          path: "mfa",
          name: "MFA",
          component: MFAuth,
        },
        {
          path: "forgot-password",
          name: "ForgotPassword",
          component: ForgotPassword,
        },
        {
          path: "reset-password",
          name: "ResetPassword",
          component: ResetPassword,
        },
        {
          path: "custom-domain",
          name: "CustomDomain",
          component: CustomDomain,
        },
      ],
    },
    {
      path: "/ks",
      component: KitchenSinkContainer,
      children: [
        {
          path: "",
          name: "Kitchen Sink",
          component: KitchenSink,
        },
      ],
    },
  ],
})

router.beforeEach((to, from, next) => {
  console.log("[OAuthDebug][Router.beforeEach] start", {
    from: from && from.fullPath,
    toPath: to && to.path,
    toFullPath: to && to.fullPath,
    href: typeof window !== "undefined" ? window.location.href : null,
    pathname: typeof window !== "undefined" ? window.location.pathname : null,
    base: router && router.options ? router.options.base : null,
  })

  // In hash-mode deployments, direct links like /xg/oauth/authorize can load with hash "#/"
  // so Vue initially sees to.path === "/". Normalize once from browser pathname/search.
  if (typeof window !== "undefined") {
    const pathname = window.location.pathname || ""
    const search = window.location.search || ""
    const oauthPath = pathname.replace(/^\/xg/, "")
    const isOauthPath = oauthPath === "/oauth/authorize" || oauthPath === "/oauth/mcp/authorize"
    const normalizedTarget = `${oauthPath}${search}`
    if (isOauthPath && to.fullPath !== normalizedTarget) {
      const hash = window.location.hash || ""
      console.warn("[OAuthDebug][Router.beforeEach] normalizing hash bootstrap route to oauth path", {
        pathname,
        search,
        hash,
        toPath: to && to.path,
        toFullPath: to && to.fullPath,
        normalizedTarget,
      })
      next(normalizedTarget)
      return
    }
  }

  // If accessing root path in browser and not already under /xg, redirect to /xg
  // This handles the case where someone navigates to / directly
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname
    // If we're at root (not under /xg), redirect to /xg
    if (currentPath === '/') {
      console.warn("[OAuthDebug][Router.beforeEach] browser pathname is '/', forcing hard redirect to /xg/", {
        toPath: to && to.path,
        toFullPath: to && to.fullPath,
        search: window.location.search,
      })
      window.location.href = '/xg/'
      return
    }
  }

  // Keep cbroute in sync with the current route so bucket/case views have params
  // before their components are created (otherwise loadAll never runs with valid ids).
  store.commit("route/ROUTE_CHANGED", { to, from })

  if (to.path === "/ks") {
    next()
  } else {
    store
      .dispatch("authenticate", store.state)
      .then((authenticated) => {
        console.log("[OAuthDebug][Router.beforeEach] auth result", {
          authenticated,
          toPath: to && to.path,
          toFullPath: to && to.fullPath,
        })
        if (authenticated) {
          if (to.path === "/" || to.path === "") {
            // Redirect to dashboard overview
            console.warn("[OAuthDebug][Router.beforeEach] authenticated + root path, redirecting to /dashboard/overview", {
              originalTo: to && to.fullPath,
            })
            next("/dashboard/overview")
          } else {
            if (to.path && to.path.startsWith("/oauth/")) {
              console.log("[OAuthDebug][Router.beforeEach] authenticated oauth route allowed through", {
                toPath: to.path,
                toFullPath: to.fullPath,
              })
            }
            next()
          }
        } else if (to.path.startsWith("/login")) {
          console.log("[OAuthDebug][Router.beforeEach] unauthenticated login route allowed", {
            toPath: to.path,
            toFullPath: to.fullPath,
          })
          next()
        } else if (to.path.startsWith("/oauth/")) {
          console.log("[OAuthDebug][Router.beforeEach] unauthenticated oauth route allowed", {
            toPath: to.path,
            toFullPath: to.fullPath,
          })
          next()
        } else {
          console.warn("[OAuthDebug][Router.beforeEach] unauthenticated non-login/non-oauth route, redirecting to /login", {
            toPath: to && to.path,
            toFullPath: to && to.fullPath,
          })
          next("/login")
        }
      })
      .catch((error) => {
        // Handle navigation guard redirect errors - these are expected and should be warnings
        if (error && error.message && error.message.includes("Redirected when going from")) {
          console.warn("Navigation guard redirect:", error.message)
          return
        }
        if (!error.message.includes("Could not find stored JWT")) {
          console.error("Authentication error", error)
        }
      })
  }
})

// Wrap router.push to automatically catch and ignore NavigationDuplicated errors
// This is a common issue when trying to navigate to the same route
const originalPush = router.push
router.push = function(location, onComplete, onAbort) {
  if (onComplete || onAbort) {
    return originalPush.call(this, location, onComplete, onAbort)
  }
  return originalPush.call(this, location).catch(err => {
    // NavigationDuplicated is harmless - it just means we tried to navigate to the current route
    if (err.name === 'NavigationDuplicated' || (err.message && err.message.includes('NavigationDuplicated'))) {
      // Silently ignore - this is expected behavior
      return Promise.resolve()
    }
    // For other errors, re-throw them
    return Promise.reject(err)
  })
}

// Also wrap router.replace for consistency
const originalReplace = router.replace
router.replace = function(location, onComplete, onAbort) {
  if (onComplete || onAbort) {
    return originalReplace.call(this, location, onComplete, onAbort)
  }
  return originalReplace.call(this, location).catch(err => {
    // NavigationDuplicated is harmless
    if (err.name === 'NavigationDuplicated' || (err.message && err.message.includes('NavigationDuplicated'))) {
      return Promise.resolve()
    }
    return Promise.reject(err)
  })
}

export default router
